import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Video, 
  Users, 
  Shield, 
  Zap, 
  Globe, 
  Star,
  Play,
  Calendar,
  MessageCircle,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { useApp } from '../../shared/context/AppContext';

const LandingPage = ({ onSignIn, onHostMeeting, onJoinMeeting }) => {
  const { onlineUsers = [] } = useApp();
  const [joinMeetingId, setJoinMeetingId] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinMeetingError, setJoinMeetingError] = useState('');

  const isUserOnline = (user) => user.isOnline !== undefined ? user.isOnline : !!user.socketId;
  const liveUsers = onlineUsers.filter(isUserOnline);
  const liveCount = liveUsers.length;
  const showLiveBadge = liveCount > 0;
  const previewPeople = showLiveBadge
    ? liveUsers.slice(0, 3)
    : [
        { id: 'you', username: 'You' },
        { id: 'alex', username: 'Alex' },
        { id: 'sam', username: 'Sam' },
      ];

  const handleJoinMeeting = (e) => {
    e.preventDefault();
    if (!joinMeetingId.trim()) {
      setJoinMeetingError('Enter a room ID to continue');
      return;
    }
    onJoinMeeting(joinMeetingId.trim());
    setShowJoinModal(false);
    setJoinMeetingId('');
    setJoinMeetingError('');
  };

  const features = [
    {
      icon: Video,
      title: "HD Video Calls",
      description: "Crystal clear video quality with adaptive streaming"
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Connect with unlimited participants seamlessly"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "End-to-end encryption for all your conversations"
    },
    {
      icon: MessageCircle,
      title: "Real-time Chat",
      description: "Instant messaging during calls with file sharing"
    },
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description: "Schedule and manage rooms with ease"
    },
    {
      icon: Globe,
      title: "Global Access",
      description: "Join from anywhere, on any device"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-950 via-blue-950/60 to-indigo-950">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="relative z-50 px-6 py-4"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <motion.div 
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
          >
            <div className="w-10 h-10 bg-gradient-to-r from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Video className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">MixNMeet</span>
          </motion.div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
            <a href="#about" className="text-gray-300 hover:text-white transition-colors">About</a>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onSignIn}
              className="px-6 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white hover:bg-white/20 transition-all"
            >
              Sign In
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-sky-400/25 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center px-4 py-2 bg-sky-500/10 border border-sky-400/30 rounded-full text-sky-300 text-sm"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Built for casual hangouts
                </motion.div>
                
                <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                  Meet up,
                  <span className="bg-gradient-to-r from-sky-300 to-blue-400 bg-clip-text text-transparent"> vibe </span>
                  and talk
                </h1>
                
                <p className="text-xl text-gray-300 leading-relaxed max-w-lg">
                  MixNMeet is a personal project for friends and communities to hop in,
                  hang out, and enjoy spontaneous conversations together.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(56, 189, 248, 0.35)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onHostMeeting}
                  className="group px-8 py-4 bg-gradient-to-r from-sky-500 to-blue-600 rounded-xl text-white font-semibold text-lg shadow-2xl hover:shadow-sky-500/25 transition-all duration-300"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Play className="w-5 h-5" />
                    <span>Start Room</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowJoinModal(true)}
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white font-semibold text-lg hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Users className="w-5 h-5" />
                    <span>Join Room</span>
                  </div>
                </motion.button>
              </div>

              <p className="text-sm text-sky-200/80">
                Want in on the fun?{' '}
                <button
                  onClick={onSignIn}
                  className="text-sky-200 hover:text-white underline underline-offset-4"
                >
                  Sign up to go live
                </button>
                .
              </p>

            </motion.div>

            {/* Right Content - Video Preview */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                {/* Main Video Card */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 shadow-2xl border border-white/10"
                >
                  <div className="aspect-video bg-gradient-to-br from-sky-500/20 to-blue-500/20 rounded-xl mb-4 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-sky-300 to-blue-500 rounded-full"></div>
                      <div>
                        <div className="text-white font-medium">You</div>
                        <div className="text-gray-400 text-sm">Host</div>
                      </div>
                    </div>
                    {showLiveBadge ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-sky-300 rounded-full animate-pulse"></div>
                        <span className="text-sky-300 text-sm">Live now</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-sky-200/80 text-sm">
                        <span>Go live</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-sky-200/80">
                      <div className="flex -space-x-2">
                        {previewPeople.map((user) => (
                          <div
                            key={user.id}
                            className="w-6 h-6 rounded-full bg-sky-500/40 border border-sky-200/40 flex items-center justify-center text-[10px] text-white"
                          >
                            {user.username?.charAt(0).toUpperCase()}
                          </div>
                        ))}
                      </div>
                      <span>
                        {showLiveBadge ? `${liveCount} in the room` : 'Group hangout ready'}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Participant Cards */}
                <motion.div
                  animate={{ x: [0, 10, 0], y: [0, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-sky-500/20 to-blue-500/20 rounded-xl border border-white/10 backdrop-blur-sm flex items-center justify-center"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-sky-300 to-blue-500 rounded-full"></div>
                </motion.div>

                <motion.div
                  animate={{ x: [0, -10, 0], y: [0, 5, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-sky-500/20 to-blue-500/20 rounded-xl border border-white/10 backdrop-blur-sm flex items-center justify-center"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-sky-300 to-blue-500 rounded-full"></div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              A cozy place to
              <span className="bg-gradient-to-r from-sky-300 to-blue-400 bg-clip-text text-transparent"> connect</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Built for casual hangouts, community chats, and quick meetups with friends.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-sky-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center px-4 py-2 bg-sky-500/10 border border-sky-400/30 rounded-full text-sky-300 text-sm mb-6"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  About MixNMeet
                </motion.div>
                
                <h2 className="text-4xl font-bold text-white mb-6">
                  Built for people who just want to
                  <span className="bg-gradient-to-r from-sky-300 to-blue-400 bg-clip-text text-transparent"> hang out</span>
                </h2>
                
                <p className="text-xl text-gray-300 leading-relaxed mb-8">
                  MixNMeet started as a fun personal project to help friends and communities
                  jump into quick, low-pressure video chats without the “enterprise” feel.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-sky-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-sky-300" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Private by default</h4>
                    <p className="text-gray-400">Your rooms stay private, and nothing is shared unless you choose to share it.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-sky-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-sky-300" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Jump in fast</h4>
                    <p className="text-gray-400">Start or join a room in seconds with a simple link or ID.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-sky-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-sky-300" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Simple and lightweight</h4>
                    <p className="text-gray-400">No extra fluff—just video, voice, and chat that works.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Team/Company Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                {/* Main Company Card */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 shadow-2xl border border-white/10"
                >
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Our Vibe</h3>
                    <p className="text-gray-400">Bring people together for easy, friendly conversations.</p>
                  </div>
                </motion.div>

                {/* Floating Elements */}
                <motion.div
                  animate={{ x: [0, 15, 0], y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-sky-500/20 to-blue-500/20 rounded-2xl border border-white/10 backdrop-blur-sm flex items-center justify-center"
                >
                  <Globe className="w-12 h-12 text-sky-300" />
                </motion.div>

                <motion.div
                  animate={{ x: [0, -15, 0], y: [0, 10, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-sky-500/20 to-blue-500/20 rounded-2xl border border-white/10 backdrop-blur-sm flex items-center justify-center"
                >
                  <Zap className="w-12 h-12 text-blue-300" />
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Company Values */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20"
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-white mb-4">Our Values</h3>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                The principles that guide everything we do
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: "Privacy First",
                  description: "Your data belongs to you. We never store, sell, or share your personal information."
                },
                {
                  icon: Users,
                  title: "Inclusive Design",
                  description: "Building technology that works for everyone, regardless of ability, location, or device."
                },
                {
                  icon: Zap,
                  title: "Innovation",
                  description: "Constantly pushing the boundaries of what's possible in digital communication."
                }
              ].map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center p-6"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-4">{value.title}</h4>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <div className="bg-gradient-to-r from-sky-500/10 to-blue-500/10 border border-sky-500/20 rounded-2xl p-12">
              <h3 className="text-3xl font-bold text-white mb-4">Ready to get started?</h3>
              <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
                Start a room and invite your people in a click.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onHostMeeting}
                  className="px-8 py-4 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-sky-500/25 transition-all"
                >
                  Start Free Room
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onSignIn}
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
                >
                  Sign Up Today
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Join Meeting Modal */}
      {showJoinModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowJoinModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-slate-800 rounded-2xl p-8 max-w-md w-full border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Join a Room</h3>
              <p className="text-gray-400">Enter the room ID to join</p>
            </div>

            <form onSubmit={handleJoinMeeting} className="space-y-6">
              <div>
                <input
                  type="text"
                  value={joinMeetingId}
                  onChange={(e) => {
                    setJoinMeetingId(e.target.value);
                    if (joinMeetingError) setJoinMeetingError('');
                  }}
                  placeholder="Enter Room ID"
                  className={`w-full px-4 py-3 bg-slate-700 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                    joinMeetingError
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-slate-600 focus:border-sky-400 focus:ring-sky-400/20'
                  }`}
                  required
                />
                {joinMeetingError && (
                  <p className="mt-2 text-xs text-red-300">{joinMeetingError}</p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowJoinModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-xl hover:shadow-lg hover:shadow-sky-500/25 transition-all"
                >
                  Join Room
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default LandingPage;